import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Invoice, InvoiceDirection } from '@prody/shared';
import { daysOverdue, invoiceOutstanding } from '@prody/shared';
import {
  Button, Code, DataTable, EmptyState, Gauge, IconExport, IconPlus, ListPane,
  MoneyText, PageHeader, Segmented, Stamp, Toolbar, vars, Workbench, type Column,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useInvoices } from '~/platform/data/queries';
import { INVOICE_TONE } from '~/platform/domain/presentation';

/* ==================================================================
   Invoices, both directions.

   Sales and purchase are one screen with a two-way switch, not two
   modules. They are the same document seen from opposite sides, and
   an accountant reconciling a day wants to flip between them without
   losing their filters.
   ================================================================== */

export default function InvoicesPage() {
  const { t } = useTranslation(['invoices', 'common']);
  const { business, format } = useBusiness();
  const [direction, setDirection] = useState<InvoiceDirection>('sales');
  const [unpaidOnly, setUnpaidOnly] = useState(false);

  const { data: invoices = [], isLoading } = useInvoices(business.id, { direction });
  const today = format.tradingDay();

  const rows = useMemo(
    () => (unpaidOnly ? invoices.filter((i) => invoiceOutstanding(i).minor > 0) : invoices),
    [invoices, unpaidOnly],
  );

  const columns = useMemo<Column<Invoice>[]>(
    () => [
      {
        key: 'number',
        header: t('title'),
        width: '130px',
        sortable: true,
        sortValue: (i) => i.number,
        cell: (i) => <Code>{i.number}</Code>,
      },
      {
        key: 'party',
        header: direction === 'sales' ? t('common:labels.customer') : t('common:labels.supplier'),
        width: 'minmax(160px, 2fr)',
        sortable: true,
        sortValue: (i) => i.partyName,
        cell: (i) => <span className="truncate">{i.partyName}</span>,
      },
      {
        key: 'issued',
        header: t('common:labels.date'),
        width: '104px',
        hideBelow: 'md',
        sortable: true,
        sortValue: (i) => i.issueDate,
        cell: (i) => <span className="num">{format.date(i.issueDate)}</span>,
      },
      {
        key: 'due',
        header: t('due'),
        width: '150px',
        sortable: true,
        sortValue: (i) => i.dueDate,
        cell: (i) => {
          const late = daysOverdue(i, today);
          return (
            <span className="row" style={vars({ '--gap': 'var(--s-3)' })}>
              <span className="num">{format.date(i.dueDate)}</span>
              {late > 0 ? (
                <Stamp tone="alert" size="sm">
                  {t('overdueBy', { count: late })}
                </Stamp>
              ) : null}
            </span>
          );
        },
      },
      {
        key: 'gross',
        header: `${t('common:labels.total')} (${format.currencySymbol()})`,
        align: 'num',
        width: '118px',
        sortable: true,
        sortValue: (i) => i.gross.minor,
        cell: (i) => <MoneyText value={i.gross} bare />,
      },
      {
        key: 'paid',
        header: t('common:labels.net'),
        width: '120px',
        hideBelow: 'lg',
        cell: (i) => (
          <Gauge
            value={i.gross.minor === 0 ? 1 : i.paid.minor / i.gross.minor}
            tone={i.paid.minor >= i.gross.minor ? 'ok' : daysOverdue(i, today) > 0 ? 'alert' : 'warn'}
            label={t('outstanding')}
          />
        ),
      },
      {
        key: 'outstanding',
        header: t('outstanding'),
        align: 'num',
        width: '118px',
        sortable: true,
        sortValue: (i) => invoiceOutstanding(i).minor,
        cell: (i) => <MoneyText value={invoiceOutstanding(i)} bare tone />,
      },
      {
        key: 'status',
        header: t('common:labels.status'),
        width: '116px',
        cell: (i) => (
          <Stamp tone={INVOICE_TONE[i.status]} weight={i.status === 'paid' || i.status === 'void' ? 'solid' : 'outline'}>
            {t(`status.${i.status}`)}
          </Stamp>
        ),
      },
    ],
    [t, format, direction, today],
  );

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={isLoading ? undefined : t('common:labels.results', { count: rows.length })}
        actions={
          <>
            <Button variant="quiet" icon={<IconExport />}>
              {t('common:actions.export')}
            </Button>
            <Button variant="primary" icon={<IconPlus />}>
              {t('issue')}
            </Button>
          </>
        }
      />

      <Toolbar>
        <Segmented<InvoiceDirection>
          label={t('title')}
          value={direction}
          onChange={setDirection}
          options={[
            { value: 'sales', label: t('sales') },
            { value: 'purchase', label: t('purchase') },
          ]}
        />
        <Button variant="quiet" size="sm" aria-pressed={unpaidOnly} onClick={() => setUnpaidOnly((v) => !v)}>
          {t('outstanding')}
        </Button>
      </Toolbar>

      <ListPane>
        <DataTable<Invoice>
          rows={rows}
          columns={columns}
          rowKey={(i) => i.id}
          rowMeta={(i) => {
            const late = daysOverdue(i, today);
            return {
              handlingClass: i.direction === 'sales' ? 'ambient' : 'neutral',
              ...(late > 30 ? { tone: 'alert' as const } : late > 0 ? { tone: 'warn' as const } : {}),
              struck: i.status === 'void',
            };
          }}
          loading={isLoading}
          caption={t('title')}
          empty={<EmptyState title={t('empty')} body={t('emptyBody')} />}
        />
      </ListPane>
    </Workbench>
  );
}
